<script setup lang="ts">
import { computed } from 'vue';
import type {
  AllocGroupIndex,
  AllocPerPhase,
  AllocPhaseIndex,
} from '../../models/SolshareSystemSpec';
import type { COUNTRY_CODE } from '@/utils/constants';
import { type PHASE } from '../../utils/constants';
import { useDeviceSpecificationStore } from '../../stores/deviceSpecificationStore';
import { getPhaseColor } from '../../constants/phase-color';

const props = withDefaults(
  defineProps<{
    phaseIndex: AllocPhaseIndex; //Parent supplies 1 or 2 or 3
    groupIndexes: AllocGroupIndex[]; //Parent supplies [0, 1, 2, 3, 4]
    phaseAllocations: AllocPerPhase;
    displayAllocationRemaining?: boolean;
    allocationsRemainingValue?: number;
    showAsKwp?: boolean;
  }>(),
  {
    displayAllocationRemaining: false,
    allocationsRemainingValue: 0,
    showAsKwp: false,
  },
);

const phaseName = computed<PHASE>(() => `L${props.phaseIndex + 1}` as PHASE);

const deviceSpecificationStore = useDeviceSpecificationStore();
const country = computed(() => deviceSpecificationStore.solshareSite?.country as COUNTRY_CODE);

const groupNumber = (groupIndex: AllocGroupIndex) => {
  return groupIndex + 1;
};
const connectionNameFieldName = (groupIndex: AllocGroupIndex) => {
  return `connection-name-${groupNumber(groupIndex)}`;
};

const allocationUnitSymbol = props.showAsKwp
  ? ' kWp' // Add space before the value, e.g. 4.30 kWp
  : '%'; // No space before the value, e.g. 93.30%
</script>

<template>
  <div class="container">
    <a-row align="stretch" class="allocations-per-phase-layout">
      <a-col :span="3" class="column__phase-label">
        <a-row class="column-content">
          <a-col
            flex="none"
            class="phase-label__phase-color-container"
            :style="{ backgroundColor: getPhaseColor(country, phaseName) }"
          ></a-col>
          <a-col flex="auto" class="phase-label__text-container">
            <div class="phase-label__text">{{ phaseName }}</div>
          </a-col>
        </a-row>
      </a-col>

      <a-col :span="21" class="column__allocations">
        <template v-for="groupIndex in groupIndexes" :key="groupIndex">
          <a-row>
            <a-col :span="15" class="connection-name-cell">
              <a-input
                readonly
                :addon-before="groupNumber(groupIndex)"
                :value="phaseAllocations[groupIndex]?.connectionName"
                size="large"
                :name="connectionNameFieldName(groupIndex)"
                :class="[
                  'connection-name',
                  { 'no-connection': !phaseAllocations[groupIndex]?.connected },
                ]"
                :data-tn="connectionNameFieldName(groupIndex)"
              />
            </a-col>
            <a-col :span="9">
              <div
                :class="[
                  'right-cell-container',
                  groupIndex === groupIndexes.length - 1 ? 'last-row' : '',
                ]"
              >
                <slot name="rightCell" :phaseIndex="phaseIndex" :groupIndex="groupIndex"></slot>
              </div>
            </a-col>
          </a-row>
        </template>
      </a-col>
    </a-row>

    <a-row v-if="displayAllocationRemaining" align="stretch">
      <div
        :class="[
          'alloc-remaining-container',
          allocationsRemainingValue !== 0 ? 'alloc-remaining__error' : '',
        ]"
      >
        <a-col :span="3"></a-col>
        <a-col :span="21">
          <a-row>
            <a-col :span="15"> Allocation remaining </a-col>
            <a-col :span="9">
              <div class="right-cell-container alloc-remaining__value">
                {{ allocationsRemainingValue }}{{ allocationUnitSymbol }}
              </div>
            </a-col>
          </a-row>
        </a-col>
      </div>
    </a-row>

    <template v-if="$slots.errorBlock">
      <div class="error-container">
        <slot name="errorBlock"></slot>
      </div>
    </template>
  </div>
</template>

<style lang="less" scoped>
.container {
  width: 100%;

  :deep(.ant-input),
  :deep(.ant-input-suffix) {
    font-size: 1rem;
    line-height: 1.8rem;
  }
}

.allocations-per-phase-layout {
  margin-bottom: 0.5rem;
}

.column__phase-label {
  border-top: 1px solid @color-mid-tone;
  border-bottom: 1px solid @color-mid-tone;

  .column-content {
    height: 100%;
  }

  .phase-label__phase-color-container {
    padding-left: @phase-color-width;
  }

  .phase-label__text-container {
    background-color: @color-primary;
    display: flex;
    align-items: center;
    justify-content: center;

    .phase-label__text {
      color: white;
    }
  }
}

.column__allocations {
  :deep(.ant-input),
  :deep(.ant-input-affix-wrapper) {
    background-color: @color-background-light;
    color: @color-primary;
  }

  .connection-name {
    // Target border styling for the stacked input labels
    :deep(.ant-input-group-addon) {
      background-color: @color-primary;
      color: white;
      font-weight: @font-weight-medium;
      border-color: @color-mid-tone;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  .connection-name.no-connection {
    :deep(.ant-input),
    :deep(.ant-input-affix-wrapper),
    :deep(.ant-input-suffix) {
      background-color: @color-bg-no-connection;
      color: @color-secondary;
      font-style: italic;
      font-size: 1rem;
    }
  }
}

.right-cell-container {
  padding-right: @content-side-margin;
  padding-left: 1rem;

  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
}

.alloc-remaining-container {
  width: 100%;
  display: flex;
  color: @color-secondary;
  text-align: end;

  &.alloc-remaining__error {
    color: @color-error;
  }

  .alloc-remaining__value {
    text-align: center;
  }
}

.error-container {
  margin-top: 0.7rem;
  margin-left: @phase-color-width;
  margin-right: @content-side-margin;

  background-color: fade(@color-error, 20%);
  border-radius: @border-radius-medium;
  border: 1px solid @color-warning;
  padding: 0.25rem 0.7rem;

  color: @color-error;
  font-weight: @font-weight-medium;
  text-align: right;
}
</style>
